import { http } from "@/lib/http";

import type {
  Cluster,
  ClusterConfig,
  ClusterCreateRequest,
  ClusterCreateResponse,
  ClusterResizeRequest,
  ClusterTemplate,
  ClusterTemplateCreateRequest,
  ClusterTemplateUpdateRequest,
  ClusterUpgradeRequest,
  NodeGroup,
  NodeGroupCreateRequest,
  NodeGroupUpdateRequest,
} from "./types";

export async function listClusterTemplates(): Promise<ClusterTemplate[]> {
  const { data } = await http.get<ClusterTemplate[]>("/k8s/cluster-templates");
  return data;
}

export async function getClusterTemplate(
  templateId: string,
): Promise<ClusterTemplate> {
  const { data } = await http.get<ClusterTemplate>(
    `/k8s/cluster-templates/${templateId}`,
  );
  return data;
}

export async function createClusterTemplate(
  payload: ClusterTemplateCreateRequest,
): Promise<ClusterTemplate> {
  const { data } = await http.post<ClusterTemplate>(
    "/k8s/cluster-templates",
    payload,
  );
  return data;
}

export async function updateClusterTemplate(
  templateId: string,
  payload: ClusterTemplateUpdateRequest,
): Promise<ClusterTemplate> {
  const { data } = await http.put<ClusterTemplate>(
    `/k8s/cluster-templates/${templateId}`,
    payload,
  );
  return data;
}

export async function deleteClusterTemplate(
  templateId: string,
): Promise<void> {
  await http.delete(`/k8s/cluster-templates/${templateId}`);
}

export async function listClusters(): Promise<Cluster[]> {
  const { data } = await http.get<Cluster[]>("/k8s/clusters");
  return data;
}

export async function getCluster(clusterId: string): Promise<Cluster> {
  const { data } = await http.get<Cluster>(`/k8s/clusters/${clusterId}`);
  return data;
}

export async function createCluster(
  payload: ClusterCreateRequest,
): Promise<ClusterCreateResponse> {
  const { data } = await http.post<ClusterCreateResponse>(
    "/k8s/clusters",
    payload,
  );
  return data;
}

export async function resizeCluster(
  clusterId: string,
  payload: ClusterResizeRequest,
): Promise<Cluster> {
  const { data } = await http.post<Cluster>(
    `/k8s/clusters/${clusterId}/resize`,
    payload,
  );
  return data;
}

export async function deleteCluster(clusterId: string): Promise<void> {
  await http.delete(`/k8s/clusters/${clusterId}`);
}

export async function getClusterConfig(
  clusterId: string,
): Promise<ClusterConfig> {
  const { data } = await http.get<ClusterConfig>(
    `/k8s/clusters/${clusterId}/config`,
  );
  return data;
}

export async function upgradeCluster(
  clusterId: string,
  payload: ClusterUpgradeRequest,
): Promise<void> {
  await http.post(`/k8s/clusters/${clusterId}/upgrade`, payload);
}

export async function listNodeGroups(
  clusterId: string,
): Promise<NodeGroup[]> {
  const { data } = await http.get<NodeGroup[]>(
    `/k8s/clusters/${clusterId}/nodegroups`,
  );
  return data;
}

export async function createNodeGroup(
  clusterId: string,
  payload: NodeGroupCreateRequest,
): Promise<NodeGroup> {
  const { data } = await http.post<NodeGroup>(
    `/k8s/clusters/${clusterId}/nodegroups`,
    payload,
  );
  return data;
}

export async function updateNodeGroup(
  clusterId: string,
  nodegroupId: string,
  payload: NodeGroupUpdateRequest,
): Promise<NodeGroup> {
  const { data } = await http.patch<NodeGroup>(
    `/k8s/clusters/${clusterId}/nodegroups/${nodegroupId}`,
    payload,
  );
  return data;
}

export async function deleteNodeGroup(
  clusterId: string,
  nodegroupId: string,
): Promise<void> {
  await http.delete(`/k8s/clusters/${clusterId}/nodegroups/${nodegroupId}`);
}
